import React, { createContext, useCallback, useContext, useState, ReactNode } from 'react';

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export interface ToastMessage {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
}

interface ToastContextValue {
  toasts: ToastMessage[];
  pushToast: (toast: Omit<ToastMessage, 'id'>) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  pushToast: () => {},
  dismissToast: () => {},
});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const pushToast = useCallback(
    (toast: Omit<ToastMessage, 'id'>) => {
      const id = `toast_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      setToasts((prev) => [...prev, { ...toast, id }]);
      window.setTimeout(() => {
        dismissToast(id);
      }, 5000);
    },
    [dismissToast],
  );

  return (
    <ToastContext.Provider value={{ toasts, pushToast, dismissToast }}>
      {children}
      {toasts.length > 0 && (
        <aside
          aria-label="Notifications"
          className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-md w-full pointer-events-none px-4 sm:px-0"
        >
          {toasts.map((t) => {
            const toneStyles =
              t.tone === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
                : t.tone === 'error'
                  ? 'border-rose-200 bg-rose-50 text-rose-950'
                  : t.tone === 'warning'
                    ? 'border-amber-200 bg-amber-50 text-amber-950'
                    : 'border-indigo-200 bg-indigo-50 text-indigo-950';

            return (
              <div
                key={t.id}
                role="status"
                className={`pointer-events-auto flex items-start justify-between gap-3 rounded-xl border p-3.5 shadow-lg transition-all ${toneStyles}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">{t.title}</p>
                  {t.description && (
                    <p className="mt-0.5 text-xs opacity-90 break-words">{t.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => dismissToast(t.id)}
                  className="text-xs font-medium opacity-70 hover:opacity-100 px-1.5 py-0.5 rounded"
                  aria-label="Dismiss notification"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </aside>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
